/**
 * Firebase auth error code → i18n key.
 *
 * Before this existed, AuthContext stored `e.message` and SignInScreen rendered
 * it raw — so users saw untranslated SDK strings like
 * "Firebase: Error (auth/invalid-credential)." in all 15 locales.
 *
 * Pure data plus one lookup: no React, no i18next import. Callers do
 * `t(mapAuthError(errorCode))`, which keeps this shareable by any surface.
 */

/**
 * App-level pseudo-codes. Namespaced `app/` so they can never collide with a
 * real Firebase code.
 */
export const AppAuthError = {
    EmailNotVerified: 'app/email-not-verified',
    ProviderGoogle: 'app/provider-google',
    ProviderApple: 'app/provider-apple',
    PasswordTooShort: 'app/password-too-short',
    PasswordCommon: 'app/password-common',
} as const;

export const AUTH_ERROR_KEYS: Record<string, string> = {
    // ── Input validation ────────────────────────────────────────────────────
    'auth/invalid-email': 'auth:email.errors.invalidEmail',
    'auth/missing-email': 'auth:email.errors.invalidEmail',
    'auth/missing-password': 'auth:email.errors.missingPassword',
    'auth/weak-password': 'auth:email.errors.weakPassword',

    // ── Sign-in ─────────────────────────────────────────────────────────────
    // With email-enumeration protection on, Firebase collapses "no such user"
    // and "wrong password" into auth/invalid-credential. The copy must stay
    // ambiguous for ALL THREE of these — saying "no account found" would hand
    // back exactly the oracle that protection exists to close.
    'auth/invalid-credential': 'auth:email.errors.invalidCredential',
    'auth/wrong-password': 'auth:email.errors.invalidCredential',
    'auth/user-not-found': 'auth:email.errors.invalidCredential',
    'auth/invalid-login-credentials': 'auth:email.errors.invalidCredential',

    // ── Sign-up ─────────────────────────────────────────────────────────────
    'auth/email-already-in-use': 'auth:email.errors.emailInUse',

    // ── Account / session state ─────────────────────────────────────────────
    'auth/user-disabled': 'auth:email.errors.userDisabled',
    'auth/requires-recent-login': 'auth:email.errors.requiresRecentLogin',
    'auth/user-token-expired': 'auth:email.errors.sessionExpired',
    'auth/invalid-user-token': 'auth:email.errors.sessionExpired',

    // ── Action links (verification / reset) ─────────────────────────────────
    'auth/expired-action-code': 'auth:email.errors.expiredActionCode',
    'auth/invalid-action-code': 'auth:email.errors.invalidActionCode',

    // ── Throttling and transport ────────────────────────────────────────────
    'auth/too-many-requests': 'auth:email.errors.tooManyRequests',
    'auth/quota-exceeded': 'auth:email.errors.tooManyRequests',
    'auth/network-request-failed': 'auth:email.errors.network',
    'auth/timeout': 'auth:email.errors.network',

    // ── Configuration ───────────────────────────────────────────────────────
    // The #1 first-run failure: the Email/Password provider is not enabled in
    // the Firebase console.
    'auth/operation-not-allowed': 'auth:email.errors.operationNotAllowed',
    'auth/internal-error': 'auth:email.errors.generic',

    // ── App-level ───────────────────────────────────────────────────────────
    [AppAuthError.EmailNotVerified]: 'auth:email.errors.emailNotVerified',
    [AppAuthError.ProviderGoogle]: 'auth:email.errors.providerGoogle',
    [AppAuthError.ProviderApple]: 'auth:email.errors.providerApple',
    [AppAuthError.PasswordTooShort]: 'auth:email.errors.passwordTooShort',
    [AppAuthError.PasswordCommon]: 'auth:email.errors.passwordCommon',
};

export const GENERIC_AUTH_ERROR_KEY = 'auth:email.errors.generic';

/** Never returns undefined — an unmapped code falls back to the generic string. */
export function mapAuthError(code?: string | null): string {
    if (!code) return GENERIC_AUTH_ERROR_KEY;
    return AUTH_ERROR_KEYS[code] || GENERIC_AUTH_ERROR_KEY;
}

/** Maps a provider id from federatedProviderHint to its guidance key. */
export function mapProviderHint(providerId?: string | null): string | null {
    if (providerId === 'google.com') return AUTH_ERROR_KEYS[AppAuthError.ProviderGoogle];
    if (providerId === 'apple.com') return AUTH_ERROR_KEYS[AppAuthError.ProviderApple];
    return null;
}
